'use client'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Button } from '@/components/ui/button'
import { CheckCircle, XCircle, RefreshCw, ExternalLink, AlertCircle, Clock } from 'react-feather'
import { Link } from '@/lib/next-shim'

interface PaymentStatusProps {
  status: 'success' | 'error' | 'pending' | 'not_started' | 'warning'
  message: string
  receiptUrl?: string | null
  onRetry?: () => void
  onStartPayment?: () => void
  meetingLinkSlug?: string
}

export function PaymentStatus({ status, message, receiptUrl, onRetry, onStartPayment, meetingLinkSlug }: PaymentStatusProps) {

  // Determine the appropriate styling and icon based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          variant: 'success',
          icon: <CheckCircle size={48} className="text-green-500" />,
          bgClass: 'bg-green-50',
          borderClass: 'border-green-200',
        }
      case 'error':
        return {
          variant: 'destructive',
          icon: <XCircle size={48} className="text-red-500" />,
          bgClass: 'bg-red-50',
          borderClass: 'border-red-200',
        }
      case 'pending':
        return {
          variant: 'default',
          icon: <Clock size={48} className="text-blue-500" />,
          bgClass: 'bg-blue-50',
          borderClass: 'border-blue-200',
        }
      case 'warning':
        return {
          variant: 'warning',
          icon: <AlertCircle size={48} className="text-yellow-500" />,
          bgClass: 'bg-yellow-50',
          borderClass: 'border-yellow-200',
        }
      case 'not_started':
      default:
        return {
          variant: 'default',
          icon: <AlertCircle size={48} className="text-gray-500" />,
          bgClass: 'bg-gray-50',
          borderClass: 'border-gray-200',
        }
    }
  }

  const statusConfig = getStatusConfig()

  return (
    <div className={`rounded-lg border ${statusConfig.borderClass} ${statusConfig.bgClass} p-6 mb-6`}>
      <div className="flex flex-col items-center text-center space-y-4">
        {/* Status Icon */}
        <div className="mb-2">{statusConfig.icon}</div>

        {/* Message */}
        <div className="text-lg font-medium mb-4">{message}</div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 justify-center">
          {status === 'success' && receiptUrl && (
            <Button variant="ghost" onClick={() => window.open(receiptUrl, '_blank')} className="border border-green-200 text-green-700 hover:bg-green-50">
              <ExternalLink size={16} className="mr-2" />
              <Trans>View receipt</Trans>
            </Button>
          )}

          {status === 'success' && meetingLinkSlug && (
            <Link href={`/meeting-links/${meetingLinkSlug}/booking/confirmation`}>
              <Button className="bg-blue-500 text-white hover:bg-blue-600"><Trans>View booking details</Trans></Button>
            </Link>
          )}

          {status === 'error' && onRetry && (
            <Button variant="ghost" onClick={onRetry} className="border border-red-200 text-red-700 hover:bg-red-50">
              <RefreshCw size={16} className="mr-2" />
              <Trans>Try again</Trans>
            </Button>
          )}

          {status === 'not_started' && onStartPayment && (
            <Button variant="default" onClick={onStartPayment} className="bg-blue-500 text-white hover:bg-blue-600">
              <Trans>Return to booking</Trans>
            </Button>
          )}

          {status === 'pending' && (
            <div className="animate-pulse flex items-center justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-blue-400 border-t-white animate-spin mr-2" />
              <span><Trans>Loading payment...</Trans></span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
